import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import recallService from '../../../services/recall.service';
import { formatPhoneNumber } from '../../smsgateway/pages/notifications/notificationUtils';
import '../activity/Activity.css';
import './RecallPools.css';

const STAGES = [
    { key: 'first', label: 'First recall', step: 1 },
    { key: 'second', label: 'Second recall', step: 2 },
    { key: 'third', label: 'Third recall', step: 3 },
    { key: 'fourth', label: 'Fourth recall', step: 4 },
];

const STATUS_META = {
    PENDING: { className: 'activity-status--waiting', icon: 'bi-hourglass-split', label: 'Pending' },
    IN_PROCESS: { className: 'activity-status--resend', icon: 'bi-arrow-repeat', label: 'In process' },
    SENT: { className: 'activity-status--sent', icon: 'bi-check-circle-fill', label: 'Sent' },
    FAILED: { className: 'activity-status--failed', icon: 'bi-x-circle-fill', label: 'Failed' },
    COMPLETED: { className: 'recall-pool-status--completed', icon: 'bi-check2-all', label: 'Completed' },
};

const MEDIUM_META = {
    EMAIL: { icon: 'bi-envelope-fill', className: 'activity-medium--email', label: 'Email' },
    SMS: { icon: 'bi-chat-text-fill', className: 'activity-medium--sms', label: 'SMS' },
    CALL: { icon: 'bi-telephone-fill', className: 'activity-medium--call', label: 'Call' },
};

const emptyStages = () => ({
    firstRecallStatus: false,
    firstRecallDate: '',
    secondRecallStatus: false,
    secondRecallDate: '',
    thirdRecallStatus: false,
    thirdRecallDate: '',
    fourthRecallStatus: false,
    fourthRecallDate: '',
});

function StatusBadge({ status }) {
    if (!status) return null;
    const meta = STATUS_META[status] || STATUS_META.PENDING;

    return (
        <span className={`activity-status ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {meta.label}
        </span>
    );
}

function MediumBadge({ mediumType }) {
    if (!mediumType) return null;
    const meta = MEDIUM_META[mediumType] || {
        icon: 'bi-question-circle',
        className: 'activity-medium--default',
        label: mediumType,
    };

    return (
        <span className={`activity-medium ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {meta.label}
        </span>
    );
}

function MetaItem({ icon, label, value, linkTo, fullWidth = false }) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    return (
        <div className={`recall-pool-modal__meta-item${fullWidth ? ' recall-pool-modal__meta-item--full' : ''}`}>
            <span className="recall-pool-modal__meta-label">
                <i className={`bi ${icon} me-1`}></i>
                {label}
            </span>
            <span className="recall-pool-modal__meta-value">
                {linkTo ? (
                    <Link to={linkTo} className="recall-pool-modal__meta-link">
                        {value}
                    </Link>
                ) : (
                    value
                )}
            </span>
        </div>
    );
}

function ModalSkeleton() {
    return (
        <div className="recall-pool-modal__skeleton">
            <div className="recall-pool-modal__skeleton-line recall-pool-modal__skeleton-line--lg" />
            <div className="recall-pool-modal__skeleton-line" />
            <div className="recall-pool-modal__skeleton-line recall-pool-modal__skeleton-line--md" />
            <div className="recall-pool-modal__skeleton-card" />
            <div className="recall-pool-modal__skeleton-card" />
        </div>
    );
}

function StageRow({ stage, sent, date, onToggle, onDateChange, disabled }) {
    const statusId = `pool-stage-${stage.key}-status`;
    const dateId = `pool-stage-${stage.key}-date`;

    return (
        <div className={`recall-pool-modal__stage${sent ? ' is-sent' : ''}`}>
            <div className="recall-pool-modal__stage-index" aria-hidden="true">
                {sent ? <i className="bi bi-check-lg"></i> : stage.step}
            </div>
            <div className="recall-pool-modal__stage-body">
                <div className="recall-pool-modal__stage-top">
                    <div className="min-width-0">
                        <div className="recall-pool-modal__stage-title">{stage.label}</div>
                        <div className="recall-pool-modal__stage-hint">
                            {sent ? 'Marked as sent' : 'Not sent yet'}
                        </div>
                    </div>
                    <div className="form-check form-switch mb-0 recall-pool-modal__stage-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={statusId}
                            checked={sent}
                            onChange={(event) => onToggle(event.target.checked)}
                            disabled={disabled}
                        />
                        <label className="form-check-label" htmlFor={statusId}>
                            Sent
                        </label>
                    </div>
                </div>
                <div className="recall-pool-modal__stage-date">
                    <label className="form-label mb-1" htmlFor={dateId}>
                        Sent date
                    </label>
                    <input
                        id={dateId}
                        type="date"
                        className="form-control form-control-sm"
                        value={date}
                        onChange={(event) => onDateChange(event.target.value)}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
}

function RecallPoolEditModal({ show, clinicId, poolId, onClose, onSaved }) {
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [record, setRecord] = useState(null);
    const [stages, setStages] = useState(emptyStages);

    useEffect(() => {
        if (!show || !clinicId || !poolId) {
            return undefined;
        }

        let active = true;
        setLoading(true);
        setLoadFailed(false);

        recallService
            .getRecallPoolById(clinicId, poolId)
            .then((res) => {
                if (!active) return;
                const data = res.data || {};
                setRecord(data);
                setStages({
                    firstRecallStatus: Boolean(data.firstRecallStatus),
                    firstRecallDate: data.firstRecallDate || '',
                    secondRecallStatus: Boolean(data.secondRecallStatus),
                    secondRecallDate: data.secondRecallDate || '',
                    thirdRecallStatus: Boolean(data.thirdRecallStatus),
                    thirdRecallDate: data.thirdRecallDate || '',
                    fourthRecallStatus: Boolean(data.fourthRecallStatus),
                    fourthRecallDate: data.fourthRecallDate || '',
                });
            })
            .catch((error) => {
                console.error(error);
                if (active) {
                    setLoadFailed(true);
                    toast.error('Failed to load recall pool entry');
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [show, clinicId, poolId]);

    const updateStage = (field, value) => {
        setStages((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await recallService.updateRecallPool(clinicId, poolId, {
                firstRecallStatus: stages.firstRecallStatus,
                firstRecallDate: stages.firstRecallDate || null,
                secondRecallStatus: stages.secondRecallStatus,
                secondRecallDate: stages.secondRecallDate || null,
                thirdRecallStatus: stages.thirdRecallStatus,
                thirdRecallDate: stages.thirdRecallDate || null,
                fourthRecallStatus: stages.fourthRecallStatus,
                fourthRecallDate: stages.fourthRecallDate || null,
            });
            toast.success('Recall stages updated');
            onSaved?.();
        } catch (error) {
            console.error(error);
            toast.error('Failed to update recall stages');
        } finally {
            setSaving(false);
        }
    };

    if (!show) {
        return null;
    }

    const fullName = `${record?.firstName || ''} ${record?.lastName || ''}`.trim();
    const title = fullName || 'Recall pool entry';
    const preferred =
        record?.preferredName && record.preferredName !== record.firstName
            ? record.preferredName
            : null;
    const patientLink = record?.patientId ? `/patient-files/${record.patientId}` : null;
    const mediumMeta = MEDIUM_META[record?.mediumType] || { icon: 'bi-person-lines-fill' };
    const sentCount = STAGES.filter(({ key }) => stages[`${key}RecallStatus`]).length;

    return (
        <Modal
            show={show}
            onHide={() => {}}
            centered
            size="lg"
            scrollable
            backdrop="static"
            keyboard={false}
            className="recall-pool-modal ecx-page"
            contentClassName="recall-pool-modal__content border-0 shadow-lg"
        >
            <Modal.Header className="recall-pool-modal__header border-0 pb-0">
                <div className="w-100">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="d-flex align-items-start gap-3 min-width-0">
                            <span className="recall-pool-modal__icon-badge flex-shrink-0">
                                <i className={`bi ${mediumMeta.icon}`}></i>
                            </span>
                            <div className="min-width-0">
                                <span className="ecx-page-chip mb-2">
                                    <i className="bi bi-collection"></i>
                                    Recall Pool
                                </span>
                                <Modal.Title as="h5" className="fw-normal text-dark mb-1 text-break">
                                    {loading && !record ? 'Loading…' : title}
                                </Modal.Title>
                                {preferred && (
                                    <div className="text-muted small mb-2">Preferred: {preferred}</div>
                                )}
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <StatusBadge status={record?.status} />
                                    <MediumBadge mediumType={record?.mediumType} />
                                    {record?.createdAt && (
                                        <span className="recall-pool-modal__header-meta">
                                            <i className="bi bi-calendar3 me-1"></i>
                                            {record.createdAt}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__close-btn flex-shrink-0"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                            disabled={saving}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="recall-pool-modal__body pt-3">
                {loading && !record ? (
                    <ModalSkeleton />
                ) : loadFailed && !record ? (
                    <div className="recall-pool-modal__empty">
                        <span className="recall-pool-modal__empty-icon">
                            <i className="bi bi-exclamation-circle"></i>
                        </span>
                        <h6 className="fw-normal text-dark mb-1">Details unavailable</h6>
                        <p className="text-muted mb-0 small">
                            We could not load this recall pool entry. Try again later.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="recall-pool-modal__summary mb-3">
                            <div className="recall-pool-modal__summary-grid">
                                <MetaItem
                                    icon="bi-person"
                                    label="Patient"
                                    value={fullName || null}
                                    linkTo={patientLink}
                                />
                                <MetaItem icon="bi-envelope" label="Email" value={record?.email} />
                                <MetaItem
                                    icon="bi-phone"
                                    label="Cell phone"
                                    value={formatPhoneNumber(record?.cellNumber)}
                                />
                                <MetaItem
                                    icon="bi-telephone"
                                    label="Home phone"
                                    value={formatPhoneNumber(record?.phoneNumber)}
                                />
                                <MetaItem
                                    icon="bi-hash"
                                    label="Recall number"
                                    value={record?.recallNumber != null ? String(record.recallNumber) : null}
                                />
                                <MetaItem
                                    icon="bi-building"
                                    label="Clinic"
                                    value={record?.clinicName || null}
                                />
                                <MetaItem icon="bi-clock-history" label="Last updated" value={record?.modifiedAt} />
                                <MetaItem
                                    icon="bi-link-45deg"
                                    label="Request ID"
                                    value={record?.recallRequestId ? String(record.recallRequestId) : null}
                                    fullWidth
                                />
                            </div>
                        </div>

                        <div className="recall-pool-modal__panel">
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                                <div>
                                    <div className="recall-pool-modal__panel-title mb-0">Recall stages</div>
                                    <p className="recall-pool-modal__panel-subtitle mb-0">
                                        Only stages can be updated for this entry
                                    </p>
                                </div>
                                <span className="recall-pool-modal__stage-count">
                                    {sentCount} of {STAGES.length} sent
                                </span>
                            </div>

                            <div className="recall-pool-modal__stages">
                                {STAGES.map((stage) => (
                                    <StageRow
                                        key={stage.key}
                                        stage={stage}
                                        sent={stages[`${stage.key}RecallStatus`]}
                                        date={stages[`${stage.key}RecallDate`]}
                                        onToggle={(checked) =>
                                            updateStage(`${stage.key}RecallStatus`, checked)
                                        }
                                        onDateChange={(value) =>
                                            updateStage(`${stage.key}RecallDate`, value)
                                        }
                                        disabled={saving}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="recall-pool-modal__footer border-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
                    <button
                        type="button"
                        className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__footer-close-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        <i className="bi bi-x-circle me-1"></i>
                        Close
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary rounded-pill px-4"
                        onClick={handleSave}
                        disabled={loading || saving || !record}
                    >
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Saving…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check2 me-1"></i>
                                Save stages
                            </>
                        )}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default RecallPoolEditModal;
